/**
 * Tela de sucesso do assistente: resumo da empresa criada NA PREVIA, as URLs
 * de webhook geradas (inclusive a do generico) e os proximos passos.
 *
 * Decisoes nao obvias:
 *  - A COMISSAO ganha um bloco proprio dizendo, por escrito, que percentual,
 *    cobranca e politica de estorno podem ser alterados depois na aba
 *    "Comissão e cobrança" da empresa. E a duvida que trava o cadastro ("e se
 *    o percentual mudar na negociacao?"); a resposta precisa estar aqui.
 *  - Diz por escrito que a empresa nao entra na lista nem no seletor do
 *    cabecalho ate o backend existir: a lista vem do catalogo ficticio e este
 *    modulo nao pode (nem deve) alterar o contexto compartilhado.
 *  - Proximos passos sao os mesmos da prontidao da regua (saude.ts): quem
 *    termina o cadastro ja sai sabendo o que falta para a primeira comissao.
 */

import { Badge, Button, CompanyLogo, Footnote, Icon, LinkButton, Panel, SectionCard } from '../../../../components/ui';
import { formatBRLFromCents, formatCount, formatDate, formatPercentFromBps } from '../../../../lib/format';
import type { EmpresaCriada } from '../../assistente';
import { FUNIL_INFO, MODELO_COBRANCA, POLITICA_ESTORNO, POLITICA_PADRAO, PROVEDOR_INFO } from '../../vocabulario';
import { CampoCopiavel } from '../CampoCopiavel';

export function TelaSucesso({ criada, onNova }: { readonly criada: EmpresaCriada; readonly onNova: () => void }): JSX.Element {
  const { company } = criada;
  const modelo = MODELO_COBRANCA[company.billingModel];
  const politica = company.refundPolicy ?? POLITICA_PADRAO;
  const infoPolitica = POLITICA_ESTORNO[politica];
  return (
    <div className="flex flex-col gap-6">
      <Panel tone="good" className="flex flex-wrap items-center gap-4 p-5">
        <span aria-hidden="true" className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-status-good text-white shadow-soft">
          <Icon name="check" size="lg" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold text-ink">{company.name} criada na prévia</h2>
          <p className="text-sm text-ink-muted">Nada foi gravado no servidor. Este é o resumo do que o cadastro real vai criar, com o status Em implantação.</p>
        </div>
        <Badge tone="good" icon="check">
          Salvo na prévia
        </Badge>
      </Panel>

      <Panel tone="brand" className="flex flex-wrap items-start gap-4 p-5">
        <span aria-hidden="true" className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand/15 text-brand">
          <Icon name="comissao" size="md" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-ink">A comissão pode ser alterada depois</h3>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-ink">
            <Badge tone="brand">{formatPercentFromBps(company.feeBps)} sobre o recuperado</Badge>
            <Badge tone={modelo.tone}>Cobrança: {modelo.curto}</Badge>
            <Badge tone={infoPolitica.tone}>Estornos: {infoPolitica.curto}</Badge>
          </p>
          <p className="mt-2 text-sm text-ink-muted">
            Percentual, modelo de cobrança e política de estorno mudam a qualquer momento em <strong className="text-ink">Empresas → {company.name} → aba Comissão e cobrança</strong>. A
            mudança aparece na hora no painel da empresa (cartão “Sua comissão”) e vale para as próximas vendas recuperadas; comissão já apurada não muda.
          </p>
        </div>
      </Panel>

      <div className="grid gap-6 lg:grid-cols-3">
        <SectionCard id="sucesso-resumo" title="Resumo" icon="empresa" className="lg:col-span-1">
          <div className="flex items-center gap-3">
            <CompanyLogo company={company} size="xl" />
            <div className="min-w-0">
              <p className="truncate text-base font-semibold text-ink">{company.name}</p>
              <p className="truncate text-sm text-ink-muted">{company.segment}</p>
            </div>
          </div>
          <dl className="mt-4 flex flex-col gap-2 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-ink-subtle">Id (prévia)</dt>
              <dd className="truncate font-mono text-xs text-ink">{company.id}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-ink-subtle">Status</dt>
              <dd>
                <Badge tone="warning" dot>
                  Em implantação
                </Badge>
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-ink-subtle">Comissão</dt>
              <dd className="text-ink">
                {formatPercentFromBps(company.feeBps)} · <Badge tone={modelo.tone}>{modelo.curto}</Badge>
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-ink-subtle">Estornos</dt>
              <dd className="text-right text-ink">{infoPolitica.curto}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-ink-subtle">Ofertas</dt>
              <dd className="text-right text-ink">
                {formatCount(criada.ofertas.length)} · {formatBRLFromCents(criada.ofertas.reduce((acc, o) => acc + o.precoCents, 0))} em catálogo
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-ink-subtle">Funis</dt>
              <dd className="text-right text-ink">{company.funnels.map((f) => FUNIL_INFO[f].label).join(' · ')}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-ink-subtle">Dono</dt>
              <dd className="truncate text-right text-ink">
                {criada.dono.nome} <span className="text-ink-muted">· {criada.dono.email}</span>
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-ink-subtle">Criada em</dt>
              <dd className="text-ink">{formatDate(company.createdAt)}</dd>
            </div>
          </dl>
        </SectionCard>

        <SectionCard id="sucesso-webhooks" title="URLs de webhook geradas" icon="link" description="Cole cada uma no provedor correspondente. O segredo informado fica no servidor; nunca é exibido." className="lg:col-span-2">
          {criada.webhooks.length === 0 ? (
            <p className="text-sm text-ink-muted">Nenhum provedor marcado.</p>
          ) : (
            <ul className="flex flex-col gap-4">
              {criada.webhooks.map((w) => (
                <li key={w.provider}>
                  <CampoCopiavel label={`${PROVEDOR_INFO[w.provider].label} · URL do webhook`} value={w.url} hint={w.provider === 'generic' ? PROVEDOR_INFO.generic.segredoHint : undefined} />
                </li>
              ))}
            </ul>
          )}
          <div className="mt-5 rounded-xl border border-line bg-elevated p-4">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Até a primeira comissão (fora da prévia)</p>
            <ol className="mt-2 flex flex-col gap-1.5 text-sm text-ink-muted">
              <li>1. Colar as URLs nos provedores e conferir os eventos de carrinho, pagamento e estorno.</li>
              <li>
                2. Conectar a instância <code className="font-mono text-xs text-ink">{criada.evolutionInstancia}</code> na Evolution API (QR code), de preferência no número do comercial.
              </li>
              <li>
                3. Publicar o assistente <span className="text-ink">{criada.vapiAssistente}</span> na Vapi com o roteiro da empresa.
              </li>
              <li>4. {criada.dono.convite ? `Enviar o convite de acesso para ${criada.dono.email}.` : 'Enviar o convite de acesso quando o dono estiver pronto.'}</li>
              <li>5. Convidar a vendedora (papel Comercial) na aba Equipe: ela atende quem responde à régua e registra venda assistida.</li>
              <li>6. Esperar o primeiro checkout abandonado: 30 minutos depois, a régua começa sozinha.</li>
            </ol>
          </div>
        </SectionCard>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <LinkButton to="/admin/empresas" variant="primary" icon="empresa">
          Ver lista de empresas
        </LinkButton>
        <Button variant="secondary" icon="mais" onClick={onNova}>
          Cadastrar outra
        </Button>
      </div>
      <Footnote>Na prévia, a empresa criada não aparece na lista nem no seletor do cabeçalho: a lista vem do catálogo fictício. No cadastro real, ela entra em ambos na hora.</Footnote>
    </div>
  );
}
